/**
 * Panneau d'administration.
 *
 * Visible seulement pour un compte administrateur (`estAdmin`, voir
 * shared/data/proprietaires.ts). Le panneau ne fait qu'AFFICHER: chaque action
 * part vers `/api/admin/...` avec le jeton, et c'est le serveur qui verifie que
 * ce jeton appartient bien a un compte administrateur. Cacher le bouton n'est
 * donc pas une securite, c'est une politesse.
 *
 * Toute action irreversible (remise a zero du monde, suppression d'un joueur,
 * restauration) passe par un ecran de confirmation explicite dans le panneau,
 * avec le nom de ce qui va disparaitre.
 */
import { Panneau, bouton, clicSonore, el, refusSonore, section } from './commun.ts'
import { journal } from '../log/journal.ts'

/** Ce que le serveur dit d'un compte. */
export interface CompteAdmin {
  nom: string
  token: string
  /** Nom du bonhomme, ou null si le compte n'a pas encore de personnage. */
  nomJoueur: string | null
  creeA: number
  vuA: number
  batiments: number
  vehicules: number
  admin: boolean
}

/**
 * Quelques regles propres au panneau. Injectees ici plutot que dans
 * client/styles/ui.css: le fichier de styles est partage, et ce panneau est le
 * seul a avoir besoin d'une liste de comptes.
 */
const CSS = `
.admin-compte {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 8px 10px; margin-bottom: 6px;
  background: rgba(255,255,255,0.05); border-radius: 10px;
}
.admin-compte__nom { font-weight: 700; }
.admin-compte__etiquette {
  font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.06em;
  padding: 2px 6px; border-radius: 6px; background: rgba(245,197,24,0.22); color: #f5c518;
}
.admin-compte__infos { flex: 1 1 180px; font-size: 0.82rem; opacity: 0.78; }
.admin-compte__actions { display: flex; gap: 6px; flex-wrap: wrap; }
.admin-compte__actions .bouton { padding: 6px 10px; font-size: 0.82rem; }
.admin-champs { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
.admin-champ {
  flex: 1 1 140px; min-width: 0; padding: 9px 10px; border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.18); background: rgba(0,0,0,0.28);
  color: inherit; font: inherit;
}
.admin-danger { color: #ff9d8f; font-weight: 600; }
.admin-etat { min-height: 1.2em; font-size: 0.86rem; margin-top: 6px; }
.admin-etat--erreur { color: #ff8a7a; }
.admin-etat--succes { color: #8fe38f; }
`

let cssPose = false

function poserCss(): void {
  if (cssPose) return
  cssPose = true
  const s = document.createElement('style')
  s.textContent = CSS
  document.head.append(s)
}

/** « il y a 3 min », « hier », ... a partir d'un horodatage epoch ms. */
function depuis(t: number): string {
  if (!Number.isFinite(t) || t <= 0) return 'jamais'
  const s = Math.max(0, Math.round((Date.now() - t) / 1000))
  if (s < 60) return 'à l’instant'
  const min = Math.round(s / 60)
  if (min < 60) return `il y a ${min} min`
  const h = Math.round(min / 60)
  if (h < 24) return `il y a ${h} h`
  const j = Math.round(h / 24)
  return j === 1 ? 'hier' : `il y a ${j} jours`
}

export class PanneauAdmin extends Panneau {
  /** Message a montrer dans le HUD. */
  onMessage: ((texte: string, genre: 'info' | 'succes' | 'erreur') => void) | null = null
  /**
   * Le monde a change sous les pieds du joueur (remise a zero, restauration,
   * affaires effacees): le jeu doit recharger. `raison` sert au journal.
   */
  onMondeChange: ((raison: string) => void) | null = null

  private token = ''
  private compte = ''
  private comptes: CompteAdmin[] = []
  private etat: HTMLElement
  private enCours = false

  constructor(parent: HTMLElement) {
    super(parent, 'admin', 'Administration', '🛠️', 'panneau-fond--admin')
    poserCss()
    this.etat = el('div', 'admin-etat')
  }

  /**
   * Ouvre le panneau pour un administrateur donne. `token` est le jeton du
   * compte: c'est lui qui prouve le droit d'administrer, cote serveur.
   */
  ouvrir(token: string, compte: string): void {
    this.token = token
    this.compte = compte
    journal.input('reglages', `panneau d administration ouvert par "${compte}"`)
    this.construire()
    this.afficher(compte)
    void this.rafraichir()
  }

  // ------------------------------------------------------------------ reseau

  private async appeler<T>(chemin: string, methode = 'GET', corps?: unknown): Promise<T | null> {
    if (this.enCours) return null
    this.enCours = true
    try {
      const r = await fetch(chemin, {
        method: methode,
        headers: {
          'x-jeu-token': this.token,
          ...(corps === undefined ? {} : { 'content-type': 'application/json' }),
        },
        ...(corps === undefined ? {} : { body: JSON.stringify(corps) }),
      })
      if (r.status === 403) {
        this.dire('Ce compte n’a pas les droits d’administration.', 'erreur')
        journal.warn('reglages', `admin refuse: ${methode} ${chemin}`)
        return null
      }
      const type = r.headers.get('content-type') ?? ''
      const donnees = type.includes('json') ? ((await r.json()) as Record<string, unknown>) : null
      if (!r.ok) {
        const message = donnees && typeof donnees['erreur'] === 'string' ? donnees['erreur'] : `Erreur ${r.status}`
        this.dire(message, 'erreur')
        journal.warn('reglages', `admin echec ${r.status}: ${methode} ${chemin} — ${message}`)
        return null
      }
      return (donnees as T) ?? ({} as T)
    } catch (e) {
      this.dire('Le serveur ne répond pas.', 'erreur')
      journal.error('reglages', `admin reseau: ${methode} ${chemin} — ${String(e)}`)
      return null
    } finally {
      this.enCours = false
    }
  }

  private dire(texte: string, genre: 'info' | 'succes' | 'erreur'): void {
    this.etat.textContent = texte
    this.etat.className = `admin-etat${genre === 'info' ? '' : ` admin-etat--${genre}`}`
    if (genre === 'erreur') refusSonore()
    this.onMessage?.(texte, genre)
  }

  /** Relit la liste des comptes et redessine. */
  private async rafraichir(): Promise<void> {
    const r = await this.appeler<{ comptes: CompteAdmin[] }>('/api/admin/comptes')
    if (!r || !Array.isArray(r.comptes)) return
    this.comptes = r.comptes
    if (this.ouvert) this.construire()
  }

  // --------------------------------------------------------------- affichage

  private construire(): void {
    this.viderCorps()
    this.viderPied()
    this.majEntete(this.compte ? `Administration — ${this.compte}` : 'Administration', '🛠️')

    this.corps.append(this.sectionComptes())
    this.corps.append(this.sectionAjout())
    this.corps.append(this.sectionMonde())
    this.corps.append(this.sectionSauvegarde())
    this.corps.append(this.etat)

    this.pied.append(bouton('↻ Rafraîchir', () => void this.rafraichir(), 'bouton--sombre'))
    this.pied.append(bouton('✔ Terminé', () => this.fermer(), 'bouton--vert'))
  }

  private sectionComptes(): HTMLElement {
    const s = section('Comptes', '👥')
    if (this.comptes.length === 0) {
      s.append(el('p', 'note', 'Aucun compte lu pour l’instant.'))
      return s
    }
    for (const c of this.comptes) {
      const ligne = el('div', 'admin-compte')
      const titre = el('span', 'admin-compte__nom', c.nom)
      ligne.append(titre)
      if (c.admin) ligne.append(el('span', 'admin-compte__etiquette', 'admin'))
      ligne.append(
        el(
          'span',
          'admin-compte__infos',
          `${c.nomJoueur ?? 'pas de personnage'} · ${c.batiments} bâtiment${c.batiments === 1 ? '' : 's'}` +
            ` · ${c.vehicules} véhicule${c.vehicules === 1 ? '' : 's'} · vu ${depuis(c.vuA)}`,
        ),
      )
      const actions = el('div', 'admin-compte__actions')
      actions.append(
        bouton(
          '🧹 Effacer ses affaires',
          () => this.confirmer(
            `Effacer les affaires de ${c.nom} ?`,
            `Ses ${c.batiments} bâtiment(s) et ${c.vehicules} véhicule(s) disparaissent. ` +
              'Son personnage et son compte restent: il se reconnecte, il est toujours là.',
            'Effacer ses affaires',
            () => this.effacerAffaires(c),
          ),
          'bouton--sombre',
        ),
      )
      if (!c.admin) {
        actions.append(
          bouton(
            '🗑 Supprimer le compte',
            () => this.confirmer(
              `Supprimer le compte ${c.nom} ?`,
              'Le compte, son personnage et tout ce qu’il a construit disparaissent. ' +
                'C’est définitif: il n’y a pas de corbeille.',
              'Supprimer le compte',
              () => this.supprimerCompte(c),
            ),
            'bouton--sombre',
          ),
        )
      }
      ligne.append(actions)
      s.append(ligne)
    }
    return s
  }

  private sectionAjout(): HTMLElement {
    const s = section('Ajouter un utilisateur', '➕')
    const champs = el('div', 'admin-champs')
    const nom = el('input', 'admin-champ')
    nom.type = 'text'
    nom.placeholder = 'Nom (lettres, chiffres, - _)'
    nom.autocomplete = 'off'
    nom.setAttribute('aria-label', 'Nom du nouveau compte')
    const mdp = el('input', 'admin-champ')
    mdp.type = 'text'
    mdp.placeholder = 'Mot de passe'
    mdp.autocomplete = 'off'
    mdp.setAttribute('aria-label', 'Mot de passe du nouveau compte')
    champs.append(nom, mdp)
    s.append(champs)
    s.append(
      bouton('➕ Créer le compte', () => void this.ajouter(nom, mdp), 'bouton--vert'),
    )
    s.append(
      el(
        'p',
        'note',
        'Le mot de passe n’est jamais enregistré en clair: le serveur n’en garde qu’une empreinte salée.',
      ),
    )
    return s
  }

  private sectionMonde(): HTMLElement {
    const s = section('Monde', '🌍')
    s.append(
      bouton(
        '💥 Remettre le monde à zéro',
        () => this.confirmer(
          'Remettre le monde à zéro ?',
          'Tous les bâtiments et tous les véhicules de tout le monde disparaissent, ' +
            'les ressources récoltées repoussent et l’horloge revient au premier matin. ' +
            'Les personnages et les comptes ne sont pas touchés. C’est définitif.',
          'Tout remettre à zéro',
          () => this.reinitialiser(),
        ),
        'bouton--sombre',
      ),
    )
    s.append(el('p', 'note', 'Les inventaires et les personnages restent intacts: seul le décor est effacé.'))
    s.append(
      el(
        'p',
        'note',
        'Filet de sécurité: la sauvegarde JSON n’est pas écrasée tout de suite. ' +
          'Tant que la sauvegarde automatique (2 min) n’a pas eu lieu, « Restaurer depuis le fichier » annule tout.',
      ),
    )
    return s
  }

  private sectionSauvegarde(): HTMLElement {
    const s = section('Sauvegarde JSON', '💾')
    s.append(
      el(
        'p',
        'note',
        'Le serveur écrit tout le monde dans data/monde.json toutes les 2 minutes et à l’arrêt.',
      ),
    )
    s.append(bouton('💾 Sauvegarder maintenant', () => void this.sauvegarder(), 'bouton--sombre'))
    const lien = el('a', 'bouton bouton--sombre', '⬇ Télécharger monde.json')
    lien.href = `/api/admin/monde.json?token=${encodeURIComponent(this.token)}`
    lien.download = 'monde.json'
    lien.addEventListener('click', () => {
      clicSonore()
      journal.input('reglages', 'admin: telechargement de monde.json')
    })
    s.append(lien)
    s.append(
      bouton(
        '⟲ Restaurer depuis le fichier',
        () => this.confirmer(
          'Restaurer le monde depuis data/monde.json ?',
          'L’état actuel est remplacé par la dernière sauvegarde. ' +
            'Tout ce qui a été fait depuis cette sauvegarde est perdu.',
          'Restaurer',
          () => this.restaurer(),
        ),
        'bouton--sombre',
      ),
    )
    return s
  }

  /**
   * Ecran de confirmation. On remplace le contenu du panneau: impossible de
   * cliquer par megarde sur autre chose, et le texte dit exactement ce qui va
   * disparaitre.
   */
  private confirmer(titre: string, detail: string, libelle: string, action: () => Promise<void>): void {
    journal.input('reglages', `admin: confirmation demandee — ${titre}`)
    this.viderCorps()
    this.viderPied()
    this.majEntete('Confirmer', '⚠️')
    const s = section(titre, '⚠️')
    s.append(el('p', 'admin-danger', 'Action irréversible.'))
    s.append(el('p', 'note', detail))
    this.corps.append(s)
    this.corps.append(this.etat)
    this.pied.append(
      bouton('← Annuler', () => {
        journal.input('reglages', `admin: annule — ${titre}`)
        this.construire()
      }, 'bouton--sombre'),
    )
    this.pied.append(
      bouton(`⚠ ${libelle}`, () => {
        journal.input('reglages', `admin: CONFIRME — ${titre}`)
        void action()
      }),
    )
  }

  // ---------------------------------------------------------------- actions

  private async ajouter(nom: HTMLInputElement, mdp: HTMLInputElement): Promise<void> {
    const n = nom.value.trim().toLowerCase()
    const m = mdp.value
    if (n.length < 2 || m.length < 3) {
      this.dire('Il faut un nom (2 caractères au moins) et un mot de passe (3 au moins).', 'erreur')
      return
    }
    journal.input('reglages', `admin: creation du compte "${n}"`)
    const r = await this.appeler<{ nom: string }>('/api/admin/compte', 'POST', { nom: n, motDePasse: m })
    if (!r) return
    nom.value = ''
    mdp.value = ''
    this.dire(`Compte « ${r.nom} » créé.`, 'succes')
    await this.rafraichir()
  }

  private async effacerAffaires(c: CompteAdmin): Promise<void> {
    const r = await this.appeler<{ batiments: number; vehicules: number }>(
      `/api/admin/affaires/${encodeURIComponent(c.nom)}`,
      'DELETE',
    )
    if (!r) {
      this.construire()
      return
    }
    this.dire(`${c.nom}: ${r.batiments} bâtiment(s) et ${r.vehicules} véhicule(s) effacés.`, 'succes')
    await this.rafraichir()
    this.onMondeChange?.(`affaires de ${c.nom} effacees`)
  }

  private async supprimerCompte(c: CompteAdmin): Promise<void> {
    const r = await this.appeler<{ nom: string }>(`/api/admin/compte/${encodeURIComponent(c.nom)}`, 'DELETE')
    if (!r) {
      this.construire()
      return
    }
    this.dire(`Compte « ${c.nom} » supprimé.`, 'succes')
    await this.rafraichir()
    this.onMondeChange?.(`compte ${c.nom} supprime`)
  }

  private async reinitialiser(): Promise<void> {
    const r = await this.appeler<{ ok: boolean }>('/api/admin/monde/reinitialiser', 'POST')
    if (!r) {
      this.construire()
      return
    }
    this.dire('Monde remis à zéro.', 'succes')
    await this.rafraichir()
    this.onMondeChange?.('monde remis a zero')
  }

  private async sauvegarder(): Promise<void> {
    journal.input('reglages', 'admin: sauvegarde forcee')
    const r = await this.appeler<{ octets: number; version: number }>('/api/admin/sauvegarder', 'POST')
    if (!r) return
    this.dire(`Sauvegarde écrite (${r.octets} octets, format v${r.version}).`, 'succes')
  }

  private async restaurer(): Promise<void> {
    const r = await this.appeler<{ source: string; rejets: string[]; batiments: number }>(
      '/api/admin/restaurer',
      'POST',
    )
    if (!r) {
      this.construire()
      return
    }
    const ecartes = Array.isArray(r.rejets) && r.rejets.length > 0 ? `, ${r.rejets.length} entrée(s) écartée(s)` : ''
    this.dire(`Monde restauré depuis le fichier ${r.source} (${r.batiments} bâtiments${ecartes}).`, 'succes')
    await this.rafraichir()
    this.onMondeChange?.('monde restaure depuis le JSON')
  }

  override detruire(): void {
    this.onMessage = null
    this.onMondeChange = null
    super.detruire()
  }
}
